import { Button, Tooltip } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import StorefrontIcon from '@mui/icons-material/Storefront';

/**
 * Federated-IdP sign-in button (round-22 Phase D follow-up).
 *
 * The three IdP integrations (Amazon, Google, Facebook) are wired
 * in CDK but the OAuth client credentials in Secrets Manager are
 * still placeholder values (TODO-005 / TODO-006 / TODO-014). Until
 * real credentials land, the buttons render disabled + gray + a
 * tooltip so the affordance is visible but the click doesn't
 * initiate a broken OAuth round-trip.
 *
 * Icons:
 *   - Facebook, Google — MUI's built-in `@mui/icons-material` icons,
 *     which ship under MIT and are safe to use in-place.
 *   - Amazon — a neutral `Storefront` MUI icon as a placeholder;
 *     MUI does not ship an Amazon-branded icon. Swap for a custom
 *     SVG dropped into `public/idp-icons/amazon.svg` once available
 *     by importing it as a `<img src="/idp-icons/amazon.svg">`
 *     inside this component.
 *
 * Tooltips wrap a `<span>` because MUI's Tooltip does not fire on
 * `disabled` elements — the wrapper span intercepts the pointer
 * events.
 */

export type IdpProvider = 'LoginWithAmazon' | 'Google' | 'Facebook';

interface IdpConfig {
  readonly label: string;
  readonly tooltip: string;
  readonly Icon: React.ComponentType<{ readonly fontSize?: 'small' | 'medium' | 'large' }>;
}

const CONFIG: Record<IdpProvider, IdpConfig> = {
  LoginWithAmazon: {
    label: 'Continue with Amazon',
    tooltip: 'Sign in via Amazon coming soon',
    Icon: StorefrontIcon,
  },
  Google: {
    label: 'Continue with Google',
    tooltip: 'Sign in via Google coming soon',
    Icon: GoogleIcon,
  },
  Facebook: {
    label: 'Continue with Facebook',
    tooltip: 'Sign in via Facebook coming soon',
    Icon: FacebookIcon,
  },
};

export interface IdpButtonProps {
  readonly provider: IdpProvider;
  readonly disabled?: boolean;
  readonly onClick?: (provider: IdpProvider) => void;
}

/**
 * Render one federated-IdP sign-in button. When `disabled` is true
 * (the default until IdP credentials land) the button is grayed
 * out and a tooltip explains the coming-soon status.
 */
export function IdpButton(props: IdpButtonProps): JSX.Element {
  const isDisabled = props.disabled ?? true;
  const { label, tooltip, Icon } = CONFIG[props.provider];

  const btn = (
    <Button
      variant="outlined"
      fullWidth
      disabled={isDisabled}
      startIcon={<Icon />}
      onClick={() => props.onClick?.(props.provider)}
      sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1 }}
    >
      {label}
    </Button>
  );

  if (!isDisabled) return btn;

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      {/* span wrapper so Tooltip fires on the disabled button */}
      <span style={{ display: 'block' }}>{btn}</span>
    </Tooltip>
  );
}
